<!-- src/components/IngredientSelector.vue -->
<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useQuery } from '@vue/apollo-composable'
import { GET_ALL_INGREDIENTS } from '../graphql/queries'
import { useBodyScrollLock } from '../composables/useBodyScrollLock'

// Props
interface Props {
  show: boolean
  selectedIngredients?: string[]
}

const props = withDefaults(defineProps<Props>(), {
  selectedIngredients: () => []
})
useBodyScrollLock(() => props.show)

// Emits
const emit = defineEmits<{
  close: []
  select: [ingredientIds: string[]]
}>()

// State
const searchQuery = ref('')
const currentPage = ref(1)
const pageSize = 10
const localSelected = ref<string[]>([...props.selectedIngredients])

// GraphQL query
const { result: ingredientsResult, loading } = useQuery(GET_ALL_INGREDIENTS)

// Computed
const allIngredients = computed(() => ingredientsResult.value?.getAllIngredients || [])

const filteredIngredients = computed(() => {
  if (!searchQuery.value.trim()) return allIngredients.value

  const query = searchQuery.value.toLowerCase()
  return allIngredients.value.filter((ingredient: any) =>
    ingredient.name.toLowerCase().includes(query) ||
    ingredient.description?.toLowerCase().includes(query)
  )
})

const totalPages = computed(() => Math.ceil(filteredIngredients.value.length / pageSize))

const paginatedIngredients = computed(() => {
  const start = (currentPage.value - 1) * pageSize
  const end = start + pageSize
  return filteredIngredients.value.slice(start, end)
})

const selectedCount = computed(() => localSelected.value.length)

// Methods
const toggleIngredient = (ingredientId: string) => {
  const index = localSelected.value.indexOf(ingredientId)
  if (index > -1) {
    localSelected.value.splice(index, 1)
  } else {
    localSelected.value.push(ingredientId)
  }
}

const selectAll = () => {
  const currentPageIds = paginatedIngredients.value.map((ing: any) => ing.id)
  currentPageIds.forEach((id: string) => {
    if (!localSelected.value.includes(id)) {
      localSelected.value.push(id)
    }
  })
}

const deselectAll = () => {
  const currentPageIds = paginatedIngredients.value.map((ing: any) => ing.id)
  localSelected.value = localSelected.value.filter(id => !currentPageIds.includes(id))
}

const clearAll = () => {
  localSelected.value = []
}

const goToPage = (page: number) => {
  currentPage.value = Math.max(1, Math.min(page, totalPages.value))
}

const close = () => {
  emit('close')
}

const apply = () => {
  emit('select', [...localSelected.value])
  close()
}

const reset = () => {
  localSelected.value = [...props.selectedIngredients]
  searchQuery.value = ''
  currentPage.value = 1
}

// Watch for search changes to reset page
watch(searchQuery, () => {
  currentPage.value = 1
})

// Watch for show prop changes to reset state
watch(() => props.show, (show) => {
  if (show) {
    reset()
  }
})
</script>

<template>
  <div v-if="show" class="modal-overlay" @click.self="close">
    <!-- Modal -->
    <div class="modal-content modal-content-large max-h-[90vh] overflow-hidden" role="dialog" aria-modal="true"
      aria-labelledby="ingredient-selector-title">
      <!-- Header -->
      <div class="modal-header">
        <div>
          <h2 id="ingredient-selector-title" class="text-xl font-bold text-gray-900">Select Ingredients</h2>
          <p class="text-gray-600 text-sm mt-1">
            {{ selectedCount }} ingredient{{ selectedCount !== 1 ? 's' : '' }} selected
          </p>
        </div>
        <button @click="close" class="modal-close-button" aria-label="Close modal">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div class="modal-body">
        <!-- Search -->
        <div class="mb-4">
          <input v-model="searchQuery" type="text" placeholder="Search ingredients..."
            class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all" />
        </div>

        <!-- Controls -->
        <div class="flex justify-between items-center p-4 bg-gray-50 border border-gray-200 rounded-lg mb-4">
          <div class="flex gap-2">
            <button @click="selectAll"
              class="px-3 py-2 text-sm bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors">
              Select Page
            </button>
            <button @click="deselectAll"
              class="px-3 py-2 text-sm bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors">
              Deselect Page
            </button>
            <button @click="clearAll"
              class="px-3 py-2 text-sm bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors">
              Clear All
            </button>
          </div>
          <div class="text-sm text-gray-600">
            Page {{ currentPage }} of {{ totalPages }}
            ({{ filteredIngredients.length }} total)
          </div>
        </div>

        <!-- Loading State -->
        <!-- Loading State -->
        <div v-if="loading" class="flex justify-center items-center py-12">
          <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
          <span class="ml-2 text-gray-600">Loading ingredients...</span>
        </div>

        <!-- Ingredients List -->
        <div v-else class="flex-1 overflow-y-auto max-h-64">
          <div v-if="paginatedIngredients.length === 0" class="text-center py-8 text-gray-500">
            <div class="text-4xl mb-2">🔍</div>
            <p>No ingredients found</p>
            <p class="text-sm">Try adjusting your search</p>
          </div>
          <div v-else class="p-4 space-y-2">
            <label v-for="ingredient in paginatedIngredients" :key="ingredient.id"
              class="flex items-center space-x-3 p-2 rounded-lg hover:bg-gray-50 cursor-pointer">
              <input type="checkbox" :checked="localSelected.includes(ingredient.id)"
                @change="toggleIngredient(ingredient.id)"
                class="h-5 w-5 text-primary-600 focus:ring-primary-500 border-gray-300 rounded transition-all" />
              <div class="flex-1">
                <div class="font-medium text-gray-900">{{ ingredient.name }}</div>
                <div v-if="ingredient.description" class="text-sm text-gray-600">
                  {{ ingredient.description }}
                </div>
                <div class="text-xs text-gray-500">
                  <span v-if="ingredient.default_unit">Unit: {{ ingredient.default_unit }}</span>
                  <span v-if="ingredient.calories_per_100g" class="ml-2">
                    {{ ingredient.calories_per_100g }} cal/100g
                  </span>
                </div>
              </div>
            </label>
          </div>
        </div>

        <!-- Pagination -->
        <div v-if="totalPages > 1"
          class="flex justify-center items-center space-x-2 mt-4 pt-4 border-t border-gray-200">
          <button @click="goToPage(currentPage - 1)" :disabled="currentPage <= 1"
            class="btn-secondary text-sm disabled:opacity-50 disabled:cursor-not-allowed">
            Previous
          </button>

          <div class="flex space-x-1">
            <button v-for="page in Math.min(totalPages, 7)" :key="page" @click="goToPage(page)" :class="[
              'px-3 py-2 text-sm rounded-lg transition-all',
              currentPage === page
                ? 'bg-primary-500 text-white'
                : 'bg-white border border-gray-300 hover:bg-gray-50'
            ]">
              {{ page }}
            </button>
          </div>

          <button @click="goToPage(currentPage + 1)" :disabled="currentPage >= totalPages"
            class="btn-secondary text-sm disabled:opacity-50 disabled:cursor-not-allowed">
            Next
          </button>
        </div>

      </div>

      <!-- Footer -->
      <div class="modal-footer">
        <button @click="close" class="btn-secondary flex-1">
          Cancel
        </button>
        <button @click="apply" class="btn-primary flex-1">
          Apply Selection ({{ selectedCount }})
        </button>
      </div>
    </div>
  </div>
</template>
